// Fichier: hooks/usePromotions.js
import { useState, useEffect } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase/config';
import { parse } from 'postcss';

export const usePromotions = () => {
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadActivePromotions();
  }, []);

  const loadActivePromotions = async () => {
    try {
      const promotionsRef = collection(db, 'promotions');
      const q = query(promotionsRef, where('isActive', '==', true));
      const querySnapshot = await getDocs(q);
      
      const now = new Date();
      const activePromos = [];
      
      querySnapshot.forEach((doc) => {
        const promo = { id: doc.id, ...doc.data() };
        const start = new Date(promo.startDate);
        const end = new Date(promo.endDate);
        
        // Ne garder que les promotions actives (date actuelle entre start et end)
        if (now >= start && now <= end) {
          activePromos.push(promo);
        }
      });
      
      setPromotions(activePromos);
    } catch (error) {
      console.error('Erreur chargement promotions:', error);
    } finally {
      setLoading(false);
    }
  };

  // Vérifier si une catégorie a une promotion
  const getPromotionForCategory = (categoryName) => {
    for (const promo of promotions) {
      if (promo.applicationType === 'all') {
        return promo;
      } else if (promo.applicationType === 'selected') {
        if (promo.selectedCategories.includes(categoryName)) {
          return promo;
        }
      } else if (promo.applicationType === 'excluded') {
        if (!promo.excludedCategories.includes(categoryName)) {
          return promo;
        }
      }
    }
    return null;
  };

  // Calculer le prix avec promotion
  const applyPromotion = (price, categoryName) => {
    const promo = getPromotionForCategory(categoryName);
    
    if (!promo) {
      return {
        originalPrice: price,
        discountedPrice: price,
        hasPromotion: false,
        discountPercent: 0
      };
    }

    // Extraire le montant numérique du prix
    const priceMatch = price.match(/(\d+(?:[.,]\d+)?)/);
    if (!priceMatch) {
      return {
        originalPrice: price,
        discountedPrice: price,
        hasPromotion: false,
        discountPercent: 0
      };
    }

    const numericPrice = parseFloat(priceMatch[1].replace(',', '.'));
    const discountAmount = (numericPrice * promo.discountPercent) / 100;
    const discountedNumericPrice = numericPrice - discountAmount;

    const discountedPrice = discountedNumericPrice.toFixed(1);

    return {
      originalPrice: price,
      discountedPrice: `${discountedPrice}€`,
      hasPromotion: true,
      discountPercent: promo.discountPercent,
      promotionName: promo.name
    };
  };

  // Calculer le prix avec promotion pour les options (ex: "90€-120€")
  const applyPromotionToOptions = (price, categoryName) => {
    const promo = getPromotionForCategory(categoryName);
    
    if (!promo || !price.includes('-')) {
      return applyPromotion(price, categoryName);
    }

    const prices = price.split('-');
    const results = prices.map(p => applyPromotion(p.trim(), categoryName));

    if (!results[0].hasPromotion) {
      return {
        originalPrice: price,
        discountedPrice: price,
        hasPromotion: false,
        discountPercent: 0
      };
    }

    return {
      originalPrice: price,
      discountedPrice: `${results[0].discountedPrice}-${results[1].discountedPrice}`,
      hasPromotion: true,
      discountPercent: promo.discountPercent,
      promotionName: promo.name
    };
  };

  return {
    promotions,
    loading,
    getPromotionForCategory,
    applyPromotion,
    applyPromotionToOptions,
    refreshPromotions: loadActivePromotions
  };
};

export default usePromotions;